import * as THREE from "three";
import { MATS } from "../materials.js";
import { makeRng } from "../builder.js";
import * as H from "./spec.js";

/**
 * The front garden of the hero house: lawn, walkway, the young maple the
 * client called out, and the mixed planting bed left of the walk.
 *
 * Layout constraints this module honours:
 *   - driveway x 1.85..5.45 is left untouched
 *   - the strip in front of the porch steps (x -0.65..0.65, z 3.7..4.6) gets
 *     only the flat walk decal, so porch.js owns the steps unobstructed
 *   - all foliage is collider-free: the cat pushes through planting.  Only
 *     things a cat would genuinely bump or perch on collide: the trunk, the
 *     stake, the pots, the ring stones, and the ankle-high bed edging
 *     (0.10 m, under the 0.23 m step height, so it never walls anything off).
 */

// Decal stack, bottom to top.  Each layer's top clears the one below by a few
// millimetres so nothing z-fights: lawn 0.025 / walk 0.040 / joints 0.046 /
// landing 0.046 (never overlaps joints) / border courses 0.050.
const LAWN_Y = 0.025;

const WALK_L = H.WALK.x - H.WALK.width / 2;   // -0.65
const WALK_R = H.WALK.x + H.WALK.width / 2;   //  0.65
const DRIVE_L = 1.85;
const DRIVE_R = 5.45;
// Lot half-width and the front-garden depth both follow spec.js; the street
// was pulled in, so the lawn now stops at the new sidewalk line.
const LOT_L = -6.6, LOT_R = 6.6, LOT_FRONT = 9.5;

// ---- planting pieces --------------------------------------------------------

/** A loose bush: 2-3 overlapping squashed blobs read as one plant at a
 *  fraction of the cost of a dense sphere.  Never solid. */
function shrub(b, rng, x, z, r, mat, capMat) {
  b.sphere(x, 0.02, z, r, mat,
    { squash: 0.72 + rng() * 0.14, segments: 6, rings: 4, solid: false });
  const extra = r > 0.5 ? 2 : 1;
  for (let i = 0; i < extra; i++) {
    const a = rng() * Math.PI * 2;
    const off = r * (0.4 + rng() * 0.25);
    b.sphere(x + Math.cos(a) * off, 0.02, z + Math.sin(a) * off,
      r * (0.55 + rng() * 0.2), (i === extra - 1 && capMat) || mat,
      { squash: 0.68 + rng() * 0.16, segments: 6, rings: 4, solid: false });
  }
}

/** Upright ornamental grass: a squat tuft with a fan of thin cones. */
function grassClump(b, rng, x, z) {
  b.cone(x, 0, z, 0.16 + rng() * 0.05, 0.34, MATS.leaf, { segments: 6 });
  const n = 5 + ((rng() * 2) | 0);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rng() * 0.8;
    const d = 0.05 + rng() * 0.09;
    b.cone(x + Math.cos(a) * d, 0.05, z + Math.sin(a) * d,
      0.035 + rng() * 0.02, 0.55 + rng() * 0.4,
      rng() < 0.5 ? MATS.lawnDry : MATS.leaf, { segments: 4 });
  }
}

/** A flowering clump: leafy mound studded with small blooms.  There is no
 *  pink in MATS, so warm red (flowerWarm) with cream (flowerPale) accents
 *  stands in for the photo's pinks and reds. */
function flowerClump(b, rng, x, z, primary = MATS.flowerWarm) {
  const r = 0.18 + rng() * 0.07;
  b.sphere(x, 0.02, z, r, MATS.leaf, { squash: 0.62, segments: 6, rings: 4 });
  const top = 0.02 + r * 1.24;
  const n = 4 + ((rng() * 3) | 0);
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, d = rng() * r * 0.7;
    b.sphere(x + Math.cos(a) * d, top - 0.1 + rng() * 0.05, z + Math.sin(a) * d,
      0.05 + rng() * 0.03, rng() < 0.78 ? primary : MATS.flowerPale,
      { segments: 5, rings: 3 });
  }
}

/** Solar path light.  Too small to collide with -- it would only snag. */
function pathLight(b, x, z) {
  b.post(x, 0, z, 0.011, 0.32, MATS.metalDark, { segments: 5, solid: false });
  b.post(x, 0.32, z, 0.03, 0.05, MATS.metalPale,
    { segments: 6, topRadius: 0.042, solid: false });
  b.post(x, 0.37, z, 0.046, 0.015, MATS.metalDark, { segments: 6, solid: false });
}

/** A flower pot (~0.4 m with plant): a real collider so it works as a
 *  stepping stone.  MATS.brick stands in for terracotta. */
function pot(b, rng, x, z) {
  const h = 0.36, rTop = 0.17;
  b.post(x, 0, z, 0.12, h, MATS.brick, {
    topRadius: rTop, segments: 9,
    collider: { x, y: 0, z, w: rTop * 1.8, h, d: rTop * 1.8 },
  });
  b.post(x, h - 0.035, z, rTop * 0.86, 0.035, MATS.soil,
    { segments: 9, solid: false });
  b.sphere(x, h - 0.02, z, 0.17, MATS.leaf, { squash: 0.75, segments: 6, rings: 4 });
  const n = 4 + ((rng() * 2) | 0);
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, d = rng() * 0.12;
    b.sphere(x + Math.cos(a) * d, h + 0.13 + rng() * 0.06, z + Math.sin(a) * d,
      0.045 + rng() * 0.025, rng() < 0.7 ? MATS.flowerWarm : MATS.flowerPale,
      { segments: 5, rings: 3 });
  }
}

/** Ankle-high stone bed edging.  0.10 m: it collides, but sits under the
 *  cat's 0.23 m step height, so it reads without ever blocking. */
function edging(b, x, z, len, axis = "x") {
  if (axis === "x") b.block(x, 0.02, z, len, 0.1, 0.13, MATS.stone, { tile: 0.3 });
  else b.block(x, 0.02, z, 0.13, 0.1, len, MATS.stone, { tile: 0.3 });
}

// ---- the called-out pieces --------------------------------------------------

/** The young maple: slender tapered trunk, modest blob crown, and the
 *  newly-planted tells -- mulch ring, stone edging, stake and tie. */
/**
 * A tapered limb between two points.
 *
 * Cylinders are built +Y-aligned, so each one is rotated onto its own
 * direction; that is what lets the crown hang off a branching frame instead of
 * floating over a bare pole.
 */
function limb(b, ax, ay, az, bx, by, bz, r0, r1, mat) {
  const dir = new THREE.Vector3(bx - ax, by - ay, bz - az);
  const len = dir.length();
  if (len < 1e-4) return;
  const g = new THREE.CylinderGeometry(r1, r0, len, 6, 1, true);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0), dir.clone().normalize()));
  g.translate((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
  b.add(g, mat, { tile: 0, collider: null });
}

/** One rounded mass of foliage, cheap enough to use a lot of. */
function foliage(b, x, y, z, r, mat, squash = 0.82) {
  b.sphere(x, y - r * squash, z, r, mat,
    { squash, segments: 7, rings: 5, solid: false });
}

function maple(b, rng) {
  const { x, z, ringR, height, trunkR, forkY, crownBase } = H.TREE;

  // Mulch disc, then a jittered ring of stones on its rim.  The stones keep
  // their rock() colliders: ~0.15 m boulders are deliberate stepping stones.
  b.post(x, 0.012, z, ringR, 0.05, MATS.mulch, { segments: 16, solid: false });
  const stones = 17;
  for (let i = 0; i < stones; i++) {
    const a = (i / stones) * Math.PI * 2 + rng() * 0.2;
    b.rock(x + Math.cos(a) * ringR, 0.01, z + Math.sin(a) * ringR,
      0.1 + rng() * 0.05, MATS.stone);
  }

  // Trunk to the fork, with a slight flare at the base.
  b.post(x, 0, z, trunkR * 1.22, 0.28, MATS.bark,
    { topRadius: trunkR, segments: 8, solid: false });
  b.post(x, 0.26, z, trunkR, forkY - 0.26, MATS.bark,
    { topRadius: trunkR * 0.82, segments: 8, solid: false });
  // Only the clear trunk is solid; the cat is stopped by wood, never leaves.
  b.collider(x, 0, z, trunkR * 2.6, forkY, trunkR * 2.6);

  // Four main limbs off the fork, each splitting once more.  The tips are what
  // the foliage masses are hung on, so the canopy has a structure under it.
  const tips = [];
  const mains = 4;
  for (let i = 0; i < mains; i++) {
    const a = (i / mains) * Math.PI * 2 + 0.55;
    const reach = 0.95 + rng() * 0.35;
    const lx = x + Math.cos(a) * reach;
    const lz = z + Math.sin(a) * reach;
    const ly = forkY + 1.15 + rng() * 0.3;
    limb(b, x, forkY - 0.1, z, lx, ly, lz, trunkR * 0.82, trunkR * 0.5, MATS.bark);
    tips.push([lx, ly, lz]);

    for (let k = 0; k < 2; k++) {
      const a2 = a + (k ? 0.55 : -0.55) + (rng() - 0.5) * 0.3;
      const r2 = reach + 0.5 + rng() * 0.4;
      const sx = x + Math.cos(a2) * r2;
      const sz = z + Math.sin(a2) * r2;
      const sy = ly + 0.9 + rng() * 0.5;
      limb(b, lx, ly, lz, sx, sy, sz, trunkR * 0.5, trunkR * 0.28, MATS.bark);
      tips.push([sx, sy, sz]);
    }
  }
  // A leader carrying the crown's top.
  limb(b, x, forkY - 0.1, z, x + 0.12, height - 0.55, z - 0.08,
    trunkR * 0.7, trunkR * 0.22, MATS.bark);

  // Foliage: masses on every branch tip, then infill between them, starting at
  // crownBase so the canopy comes well down the trunk rather than sitting on
  // top of it like a lollipop.
  const greens = [MATS.leaf, MATS.leafDark, MATS.leafWarm];
  for (const [tx, ty, tz] of tips) {
    foliage(b, tx, ty + 0.42, tz, 0.62 + rng() * 0.26,
      greens[(rng() * 3) | 0]);
  }
  const skirtR = ringR * 0.92;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.3;
    foliage(b, x + Math.cos(a) * skirtR * (0.7 + rng() * 0.3),
      crownBase + rng() * 0.55,
      z + Math.sin(a) * skirtR * (0.7 + rng() * 0.3),
      0.58 + rng() * 0.2, greens[(rng() * 3) | 0]);
  }
  for (let i = 0; i < 8; i++) {
    const a = rng() * Math.PI * 2;
    const rad = rng() * 1.15;
    foliage(b, x + Math.cos(a) * rad, crownBase + 1.1 + rng() * 3.0,
      z + Math.sin(a) * rad, 0.7 + rng() * 0.4, greens[(rng() * 3) | 0]);
  }
  foliage(b, x + 0.1, height, z - 0.05, 0.85, MATS.leaf, 0.78);

  // The shrub the client asked for, inside the mulch on the tree's left
  // (screen-left from the street is -X).  Non-solid, like all planting.
  const bx = x - ringR * 0.62, bz = z + 0.18;
  for (const [dx, dy, dz, r] of [
    [0, 0, 0, 0.46], [0.26, -0.06, 0.14, 0.34], [-0.22, -0.05, -0.12, 0.33],
  ]) {
    b.sphere(bx + dx, 0.02 + dy, bz + dz, r, MATS.boxwood,
      { squash: 0.86, segments: 7, rings: 5, solid: false });
  }

  b.mark("yard.tree", x, 0, z);
}

/** The mixed bed left of the walkway: mulch, edging, big bushes at the porch,
 *  low mounds and flowers at the front, grasses between -- and it carries on
 *  past the porch to the left lot edge. */
function walkBed(b, rng) {
  const back = H.PORCH.front + 0.02;      // meets the porch face

  // main run: porch front out to z 5.7, walk edge across to past the porch
  b.slab(-3.14, 0.012, (back + 5.7) / 2, 4.92, 5.7 - back, MATS.mulch,
    { thickness: 0.045 });
  // left continuation, wrapping toward the house corner
  b.slab(-6.15, 0.012, 4.25, 1.2, 3.7, MATS.mulch, { thickness: 0.045 });

  // stone edging in hand-laid segments (front run, walk side, left patch)
  edging(b, -1.4, 5.76, 1.5);
  edging(b, -2.95, 5.79, 1.45);
  edging(b, -4.5, 5.75, 1.5);
  edging(b, -0.72, 4.7, 1.9, "z");
  edging(b, -6.15, 6.12, 1.15);

  // back row: the big rounded bushes pushed up against the porch
  shrub(b, rng, -1.35, 4.3, 0.62, MATS.hedge, MATS.leafDark);
  shrub(b, rng, -3.0, 4.2, 0.55, MATS.leafDark, MATS.leaf);
  shrub(b, rng, -4.7, 4.3, 0.68, MATS.hedge, MATS.leaf);

  // upright ornamental grasses between the rows
  grassClump(b, rng, -2.2, 4.65);
  grassClump(b, rng, -4.0, 4.95);
  grassClump(b, rng, -5.35, 4.5);

  // front row: low mounds, a couple spilling past the edging (slightly
  // overgrown is the character of this bed, not a tidy clipped row)
  const mounds = [
    [-0.95, 5.15, 0.3, MATS.hedge],
    [-1.9, 5.55, 0.36, MATS.leaf],       // spills over the edging
    [-2.85, 5.2, 0.32, MATS.leafDark],
    [-3.7, 5.45, 0.34, MATS.hedge],
    [-4.5, 5.25, 0.3, MATS.leaf],
    [-5.35, 5.5, 0.33, MATS.hedge],      // spills over the edging
  ];
  for (const [x, z, r, mat] of mounds) shrub(b, rng, x, z, r, mat);

  // flowering clumps, warm red with cream accents (the photo's pinks)
  flowerClump(b, rng, -1.5, 5.42);
  flowerClump(b, rng, -2.5, 5.5);
  flowerClump(b, rng, -3.3, 5.45, MATS.flowerPale);
  flowerClump(b, rng, -4.15, 5.5);
  flowerClump(b, rng, -2.6, 4.78);
  flowerClump(b, rng, -4.45, 4.6);

  // the left continuation past the porch
  shrub(b, rng, -6.1, 3.35, 0.68, MATS.leafDark, MATS.hedge);
  shrub(b, rng, -6.35, 4.65, 0.45, MATS.hedge);
  grassClump(b, rng, -5.9, 5.3);
  flowerClump(b, rng, -6.3, 5.6);
  shrub(b, rng, -5.8, 5.85, 0.3, MATS.leaf);
}

// ---- hard landscaping -------------------------------------------------------

function walkway(b) {
  // There is no path to the street.  Two steps off the porch land on a small
  // apron that curves straight into the driveway, which is how the house
  // actually reads -- a long straight walk to the kerb was invented.
  const W = 1.35, R = 1.9, z0 = 4.55;

  // landing at the foot of the steps, a course wider than the apron
  b.slab(H.WALK.x, 0.026, 4.5, 2.05, 0.95, MATS.paver, { thickness: 0.02 });

  // quarter sweep from heading away-from-the-house round to heading at the
  // drive; each paver is yawed to the local tangent so the curve is smooth
  // rather than a staircase of axis-aligned boxes.
  const n = 14;
  for (let i = 0; i <= n; i++) {
    const t = i / n, a = t * (Math.PI / 2);
    const pw = W - t * 0.12;
    const px = R - R * Math.cos(a), pz = z0 + R * Math.sin(a);
    const g = new THREE.BoxGeometry(pw, 0.02, 0.44);
    g.rotateY(a);
    g.translate(px, 0.028, pz);
    // Register the yawed rect, not the bounding box -- see Builder.add.  The
    // walk is the first thing the cat steps onto leaving the porch, so it is
    // the most-noticed 5 cm of sinking in the level.
    b.add(g, MATS.paver, { collider: null, tile: 1.2, ao: 0,
                           surface: { x: px, z: pz, w: pw, d: 0.44, yaw: a } });
  }

  // border course along the outside of the sweep, for a finished edge
  for (let i = 0; i <= n; i++) {
    const t = i / n, a = t * (Math.PI / 2);
    const rr = R + (W - t * 0.12) / 2 - 0.03;
    const g = new THREE.BoxGeometry(0.05, 0.02, 0.44);
    g.rotateY(a);
    g.translate(R - rr * Math.cos(a), 0.034, z0 + rr * Math.sin(a));
    b.add(g, MATS.concreteDark, { collider: null, tile: 1.2, ao: 0 });
  }
}

function lawns(b) {
  const g = MATS.lawn;
  const t = { thickness: LAWN_Y };
  // left of the porch, house face to the sidewalk
  b.slab((LOT_L + H.PORCH.minX) / 2, 0, (H.FACE + LOT_FRONT) / 2,
    H.PORCH.minX - LOT_L, LOT_FRONT - H.FACE, g, t);
  // in front of the porch, left of the walk (the bed lies on top of this)
  b.slab((H.PORCH.minX + WALK_L) / 2, 0, (H.PORCH.front + LOT_FRONT) / 2,
    WALK_L - H.PORCH.minX, LOT_FRONT - H.PORCH.front, g, t);
  // Nothing between the porch and the driveway: the client wants the drive
  // brought right up to the entry, with no grass strip and no bed.
  // right of the driveway, out to the lot edge
  b.slab((DRIVE_R + LOT_R) / 2, 0, (H.FACE + LOT_FRONT) / 2,
    LOT_R - DRIVE_R, LOT_FRONT - H.FACE, g, t);
}

// ---- entry point --------------------------------------------------------------

export function build(b) {
  const rng = makeRng(0x5eedf00d);   // constant seed: identical every build

  lawns(b);
  walkway(b);
  maple(b, rng);
  walkBed(b, rng);

  // pots flanking the step landing and one along the walk edge
  pot(b, rng, -1.15, 5.05);
  pot(b, rng, 1.05, 5.0);
  pot(b, rng, 0.88, 7.9);

  // Solar lights dotted along the bed front and up the lawn.  The x = -0.8
  // line used to carry a fourth at z = 11.8, from back when a straight walk
  // ran to the kerb; walkway() replaced that with the sweep into the drive,
  // and the crescent then brought the sidewalk in to 10.5 here -- so the last
  // light was left standing out on the public pavement, lighting nothing.
  pathLight(b, -1.3, 5.95);
  pathLight(b, -3.1, 5.97);
  pathLight(b, -4.9, 5.95);
  pathLight(b, -0.8, 7.4);
  pathLight(b, -0.8, 9.6);
  pathLight(b, 0.8, 8.6);
}
